import {
  OwnershipModelHelper,
  type OwnershipModel,
  type Relation,
} from '../model';
import { ForestHelper } from './forest-helper';
import { LayoutGeometryHelper as G } from './layout-geometry-helper';
import type {
  ChipDraft,
  PlacedNode,
  ReferenceChip,
  ReferenceChipDirection,
} from './structure-layout.types';
import { TextMeasureHelper, type TextMeasurer } from './text-measure-helper';

class ReferenceChipHelper {
  static readonly MAX_SIDE_WIDTH = 320;

  static readonly DIRECTION_RANK: Readonly<
    Record<ReferenceChipDirection, number>
  > = { ownedBy: 0, owns: 1 };

  static createDrafts(
    relations: ReadonlyArray<Relation>,
    model: Readonly<OwnershipModel>,
    loopIds: ReadonlySet<string>,
    measurer: Readonly<TextMeasurer>
  ): Map<number, ChipDraft[]> {
    const nameOf = (id: number) => model.entitiesById.get(id)?.name ?? '';
    const draftsOf = new Map<number, ChipDraft[]>();
    const add = (entityId: number, draft: Omit<ChipDraft, 'width'>) => {
      const width =
        Math.ceil(measurer.measure(draft.text, TextMeasureHelper.CHIP_FONT)) +
        2 * G.CHIP_PADDING_X;
      draftsOf.set(entityId, [
        ...(draftsOf.get(entityId) ?? []),
        { ...draft, width },
      ]);
    };
    relations.forEach((relation) => {
      const share = OwnershipModelHelper.getShareLabel(relation);
      const loop = loopIds.has(relation.id) ? ' · loop' : '';
      add(relation.ownedId, {
        relationId: relation.id,
        counterpartId: relation.ownerId,
        direction: 'ownedBy',
        text: `Also owned by ${nameOf(relation.ownerId)}${share ? ` · ${share}` : ''}${loop}`,
      });
      add(relation.ownerId, {
        relationId: relation.id,
        counterpartId: relation.ownedId,
        direction: 'owns',
        text: `Also owns ${share ? `${share} of ` : ''}${nameOf(relation.ownedId)}${loop}`,
      });
    });
    draftsOf.forEach((drafts) =>
      drafts.sort(
        (a, b) =>
          ReferenceChipHelper.DIRECTION_RANK[a.direction] -
            ReferenceChipHelper.DIRECTION_RANK[b.direction] ||
          ForestHelper.COLLATOR.compare(
            nameOf(a.counterpartId),
            nameOf(b.counterpartId)
          ) ||
          a.counterpartId - b.counterpartId
      )
    );
    return draftsOf;
  }

  static besideNode(
    drafts: ReadonlyArray<ChipDraft>,
    node: Readonly<PlacedNode>
  ): ReferenceChip[] {
    return drafts.map((draft, i) =>
      ReferenceChipHelper.place(
        draft,
        node.entityId,
        node.x + node.width + G.CHIP_INSET,
        node.y + i * (G.CHIP_HEIGHT + G.CHIP_GAP),
        ReferenceChipHelper.MAX_SIDE_WIDTH
      )
    );
  }

  static belowNode(
    drafts: ReadonlyArray<ChipDraft>,
    node: Readonly<PlacedNode>
  ): ReferenceChip[] {
    return drafts.map((draft, i) =>
      ReferenceChipHelper.place(
        draft,
        node.entityId,
        node.x + G.CHIP_INSET,
        node.y + node.height + G.CHIP_GAP + i * (G.CHIP_HEIGHT + G.CHIP_GAP),
        node.width - 2 * G.CHIP_INSET
      )
    );
  }

  static getStackHeight(count: number): number {
    return count * (G.CHIP_HEIGHT + G.CHIP_GAP);
  }

  private static place(
    draft: Readonly<ChipDraft>,
    entityId: number,
    x: number,
    y: number,
    maxWidth: number
  ): ReferenceChip {
    return {
      ...draft,
      id: `chip:${draft.direction}:${draft.relationId}`,
      entityId,
      x,
      y,
      width: Math.min(draft.width, maxWidth),
      height: G.CHIP_HEIGHT,
    };
  }
}

export { ReferenceChipHelper };
